import { ElementType } from "react";
import { Button, styled } from "@mui/material";

import { iconInSx } from "../styles/motion";

// Canonical popup CTA buttons — matches the EFTActionButton bid dialog exactly. Every
// popup's primary action uses PopupActionButton; while a transaction/signature is in
// flight the button is swapped (not re-styled) for PopupProcessingButton with a white
// 20px CircularProgress inside, so "busy" (#333) reads differently from "blocked"
// (translucent black disabled state).

// Phones (below the theme's sm breakpoint, 560px): these labels are sentences — "Review term
// challenge — no gas needed", "Yes, uphold and keep the rewards frozen" — and on a narrow handset
// they do not fit on one line.
//
// What actually breaks there is the theme's own `MuiButton.styleOverrides.sizeLarge`
// (styles/theme.ts), which sets `width: 180px; min-width: max-content` on every Button and, being
// injected after this styled class, wins — that `max-content` floor is why an over-long label
// pushes the pill wider than the popup instead of wrapping inside it, and why call sites resort
// to inline `style={{ width: "100%" }}`. Hence `&&`: doubling the class outranks it without an
// inline style. The pill is pinned to its container, the label wraps inside, and the box grows to
// fit rather than letting the second line spill out of the fixed 40px height. Anything that still
// fits on one line is unchanged — minHeight holds the canonical 40px pill.
//
// Phones only: desktop keeps the popup buttons exactly as they render today.
const wrapOnPhones = {
  "&&": {
    "@media (max-width: 560px)": {
      width: "100%",
      minWidth: 0,
      maxWidth: "100%",
      height: "auto",
      minHeight: 40,
      padding: "10px 16px",
      lineHeight: 1.3,
      whiteSpace: "normal",
    },
  },
};

export const PopupActionButton = styled(Button)({
  height: 40,
  width: "100%",
  backgroundColor: "black",
  color: "white",
  borderRadius: 20,
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  textTransform: "none",
  border: "none",
  padding: "11px 0 12px 0",
  ...wrapOnPhones,
  "&:hover": {
    backgroundColor: "#333",
    border: "none",
    color: "white",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    color: "rgba(255, 255, 255, 0.6)",
  },
});

export const PopupProcessingButton = styled(Button)({
  height: 40,
  width: "100%",
  backgroundColor: "#333",
  color: "white",
  borderRadius: 20,
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  border: "none",
  ...wrapOnPhones,
  "&:hover": {
    border: "none",
    backgroundColor: "#333",
  },
  // The spinner arrives with a transitions-dev icon swap (grows from 0.25 and fades in, 250ms)
  // rather than popping into the pill that replaced the CTA. The svg, not the root: the root
  // already runs CircularProgress's own rotation, which a second animation would replace.
  "& .MuiCircularProgress-svg": iconInSx,
});

// Secondary / dismissive action ("Cancel"), sitting directly under the primary CTA.
//
// Every one of these has to state its own background: the theme gives MuiButton
// `defaultProps: { variant: "contained", color: "primary" }` (styles/theme.ts), whose variant
// paints the background with `text.primary` — black. A bare <Button sx={{ color: "black" }}>
// therefore renders black-on-black and the label disappears. Same reason the hover block
// re-declares both colours: the theme's contained hover inverts them.
export const PopupGhostButton = styled(Button)({
  height: 40,
  width: "100%",
  backgroundColor: "transparent",
  color: "black",
  borderRadius: 20,
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  textTransform: "none",
  border: "none",
  boxShadow: "none",
  padding: "11px 0 12px 0",
  ...wrapOnPhones,
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    color: "black",
    border: "none",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "transparent",
    color: "rgba(0, 0, 0, 0.38)",
    border: "none",
  },
});

// Destructive ghost action ("Challenge this deed") — same shape as PopupGhostButton, red label.
export const PopupDangerButton = styled(Button)({
  height: 40,
  width: "100%",
  backgroundColor: "transparent",
  color: "#B00020",
  borderRadius: 20,
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  textTransform: "none",
  border: "none",
  boxShadow: "none",
  padding: "11px 0 12px 0",
  ...wrapOnPhones,
  "&:hover": {
    backgroundColor: "rgba(176, 0, 32, 0.06)",
    color: "#B00020",
    border: "none",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "transparent",
    color: "rgba(176, 0, 32, 0.38)",
    border: "none",
  },
});

// Dashed file picker, used as `component="label"` with a hidden <input type="file"> inside.
// Left-aligned like an input rather than centred like a CTA, and the filename ellipsises
// instead of wrapping the button to two lines.
// styled() drops Button's polymorphic `component` prop from the public type, so it is
// re-declared here — this button is always rendered as a <label> wrapping a hidden input.
export const FileSelectButton = styled(Button)<{ component?: ElementType }>({
  width: "100%",
  minHeight: 48,
  justifyContent: "flex-start",
  gap: 10,
  padding: "12px 16px",
  backgroundColor: "#fff",
  color: "#000",
  border: "1px dashed #999",
  borderRadius: 12,
  boxShadow: "none",
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  textTransform: "none",
  "& .MuiButton-startIcon": {
    marginLeft: 0,
    marginRight: 0,
    color: "#666",
  },
  "& .file-name": {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  // Same theme override as the pills above (`sizeLarge`'s `min-width: max-content`), with a
  // sharper edge here: it sizes the picker to the *file name*, so on a phone a long one would
  // stretch it past the popup instead of ellipsising inside it. Pin it to the container — and
  // unlike the pills this label keeps `nowrap`, since ellipsis is the point.
  "&&": {
    "@media (max-width: 560px)": {
      width: "100%",
      minWidth: 0,
      maxWidth: "100%",
      padding: "12px 16px",
    },
  },
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.03)",
    color: "#000",
    border: "1px dashed #666",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#fff",
    color: "rgba(0, 0, 0, 0.38)",
    border: "1px dashed #ddd",
  },
});
